import type { FC } from 'react';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Typography } from '@interstate/components/Typography';
import type { CoBuyer, IShopper } from '@makemydeal/dr-platform-types';
import { useCustomerCardSelector } from '../../hooks/useCustomerCardSelector';
import * as customerCardSelectors from '../../store/selectors';
import { CARD_OPERATION_KIND } from '../../constants';
import { BannerContainer } from './search.styles';
import type { CardOperationKind } from '../../types/cardOperation.types';

const BUSINESS_CONTEXT =
    'Search for an existing business first to copy its record. If no match is found, you can create a new one.\n' +
    'This converts the deal to a business customer. The buyer becomes a signer, personal fields are replaced with ' +
    'business ones, and sensitive personal data is permanently removed.';

const REPLACING_LABEL: Partial<Record<CardOperationKind, string>> = {
    [CARD_OPERATION_KIND.CHANGE_BUYER]: 'Replacing Current Buyer',
    [CARD_OPERATION_KIND.CHANGE_CO_BUYER]: 'Replacing Current Co-Buyer'
};

const participantName = (participant?: IShopper | CoBuyer | null) =>
    participant ? [participant.firstName, participant.lastName].filter(Boolean).join(' ') : '';

/**
 * The context shown above the search results. Reads the running operation and its target participant
 * itself, so what a descriptor carries stays static per kind.
 */
const SearchBanner: FC = () => {
    const theme = useInterstateTheme();
    const kind = useCustomerCardSelector(customerCardSelectors.getOperationKind);
    const participant = useCustomerCardSelector(customerCardSelectors.getOperationTargetParticipant);

    if (kind === CARD_OPERATION_KIND.CONVERT_TO_BUSINESS) {
        return (
            <Typography variant="body-md" data-testid="search-banner" sx={{ display: 'block', marginBottom: '1rem' }}>
                {BUSINESS_CONTEXT}
            </Typography>
        );
    }

    const label = kind ? REPLACING_LABEL[kind] : undefined;
    const name = participantName(participant);
    if (!label || !name) return null;

    return (
        <BannerContainer data-testid="search-banner">
            <Typography
                variant="body-sm"
                tag="span"
                sx={{ display: 'block', color: theme.tokens.BaseColorGray600, lineHeight: 1, marginBottom: '0.5rem' }}
            >
                {label}
            </Typography>
            <Typography variant="h4" tag="span" sx={{ display: 'block' }}>
                {name}
            </Typography>
        </BannerContainer>
    );
};

export default SearchBanner;
